<template>
    <div class="display-conditional">
        <div class="text">
            <div class="flex">
                <label class="required">
                    Seismic Force Resisting System
                </label>
                <div class="help">
                    <modal tooltip="calculations for R and Omega" result="/docs/ASCE_7-22_Table_12-2-1_Reference_Information.pdf" text="" link-classes="fa fa-question"/>
                </div>
            </div>
            <div class="select">
                <select 
                    v-model="systemValue" 
                    @change="systemChange"
                    required 
                >
                    <option v-for="label, value in systemOptions" :value="value" v-text="label"/>
                </select>
            </div>
            <p class="note" v-text="systemText"/>
        </div>
        <manual-inputs v-if="showManualInput" :input="manualInputs" @updateManualValues="updateManualValues" />
        <div class="text" v-else>
            <div v-if="showSubsystem">
                <div class="flex">
                    <label class="required">
                        Seismic Force Resisting Subsystem
                    </label>
                </div>
                <div class="select">
                    <select 
                        v-model="subsystemValue" 
                        @change="calculateValues"
                        required 
                    >
                        <option value=""/>
                        <option v-for="opt in subSystemOptions" :value="opt.value" v-text="opt.label"/>
                    </select>
                </div>
                <p class="note" v-text="subSystemText" />
            </div>
        </div>
    </div>
</template>

<script>
import Modal from '../_modal';
import ManualInputs from './manual-inputs';

export default {
    props: {
        standardId: {type: [String,Number]},
        system: {type: String},
        subsystem: {type: [String,Number]},
        r: {type: [String,Number]},
        omega: {type: [String,Number]},
        manualInputPropValues: {
            type: Array,
            default: () => {
                return [];
            }
        },
    },
    components: {
        Modal,
        ManualInputs,
    },
    computed: {
        computedRValue() {
            if (!this.systemValue) {
                return null;
            }
            if (this.systemValue == 'F') {
                return 4.5;
            }
            if (this.systemValue == 'H') {
                return 3;
            }
            if (this.systemValue == 'M') {
                return this.rValue;
            }
            if (this.subSystemOptions.length == 0) {
                return null;
            }

            const sub = this.subSystemOptions.find(v => v.value == this.subsystemValue);

            return sub ? sub.rVal : null;
        },
        computedOmegaValue() {
            if (!this.systemValue) {
                return null;
            }
            if (this.systemValue == 'F') {
                return 2.5;
            }
            if (this.systemValue == 'H') {
                return 3;
            }
            if (this.systemValue == 'M') {
                return this.omegaValue;
            }
            if (this.subSystemOptions.length == 0) {
                return null;
            }

            const sub = this.subSystemOptions.find(v => v.value == this.subsystemValue);

            return sub ? sub.omega : null;
        },
        showSubsystem() {
            return this.subSystemOptions.length > 0;
        },
        showManualInput() {
            return this.systemValue == 'M';
        },
        systemText() {
            return 'Type of System per ASCE 7-22 Table 12.2-1';
        },
        subSystemText() {
            return 'Type of System per ASCE 7-22 Table 12.2-1';
        },
        subSystemOptions() {
            if (this.systemValue == 'A') {
                return this.subsystemA;
            }
            if (this.systemValue == 'B') {
                return this.subsystemB;
            }
            if (this.systemValue == 'C') {
                return this.subsystemC;
            }
            if (this.systemValue == 'D') {
                return this.subsystemD;
            }
            if (this.systemValue == 'E') {
                return this.subsystemE;
            }
            if (this.systemValue == 'G') {
                return this.subsystemG;
            }

            return [];
        },
        isValid() {
            if (!this.rValue) {
                return false;
            }
            if (!this.omegaValue) {
                return false;
            }

            return true;
        },
    },
    methods: {
        systemChange() {
            this.subsystemValue = null;
            this.rValue = null;
            this.omegaValue = null;
            this.manualInputs = [];

            this.calculateValues();
        },
        calculateValues() {
            this.rValue = this.computedRValue;
            this.omegaValue = this.computedOmegaValue;

            this.emitValues();
        },
        emitValues() {
            const params = {
                system: this.systemValue,
                subsystem: this.subsystemValue,
                rValue: this.rValue,
                omega: this.omegaValue,
                manualInputValues: this.manualInputs
            };
            this.$emit('set-system-params', params);
        },
        updateManualValues(values) {
            // Set the correct rValue and omega
            this.setManualValues(values);
            this.manualInputs = values;

            this.emitValues();
        },
        setManualValues(values) { 
            let actual = {r: null, o: null};

            if (values.length > 0) {
                let ratio = 100;
                let n = null;

                values.forEach(v => {
                    n = v.r/v.o;

                    if (ratio > n) {
                        actual.r = v.r;
                        actual.o = v.o;

                        ratio = n;
                    }
                });
            }

            this.rValue = actual.r;
            this.omegaValue = actual.o;
        }
    },
    mounted() {
        this.systemValue = this.system;
        this.subsystemValue = this.subsystem;
        this.rValue = this.r;
        this.omegaValue = this.omega;
        this.manualInputs = this.manualInputPropValues;
    },
    data() {
        return {
            systemValue: null,
            subsystemValue: null,
            rValue: null,
            omegaValue: null,
            manualInputs: [],
            systemOptions: {
                A:'A. BEARING WALL SYSTEMS',
                B:'B. BUILDING FRAME SYSTEMS',
                C:'C. MOMENT-RESISTING FRAME SYSTEMS',
                D:'D. DUAL SYSTEMS WITH SPECIAL MOMENT FRAMES CAPABLE OF RESISTING AT LEAST 25% OF PRESCRIBED SEISMIC FORCES',
                E:'E. DUAL SYSTEMS WITH INTERMEDIATE MOMENT FRAMES CAPABLE OF RESISTING AT LEAST 25% OF PRESCRIBED SEISMIC FORCES',
                F:'F. SHEAR WALL-FRAME INTERACTIVE SYSTEM WITH ORDINARY REINFORCED CONCRETE MOMENT FRAMES AND ORDINARY REINFORCED CONCRETE SHEAR WALLS',
                G:'G. CANTILEVERED COLUMN SYSTEMS DETAILED TO CONFORM TO THE REQUIREMENTS FOR:',
                H:'H. STEEL SYSTEMS NOT SPECIFICALLY DETAILED FOR SEISMIC RESISTANCE, EXCLUDING CANTILEVER COLUMN SYSTEMS',
                M:'MANUAL ENTRY'
            },
            subsystemA: [
                {
                    value:1,
                    label:'1. Special reinforced concrete shear walls',
                    rVal:7,
                    omega:3
                },
                {
                    value:2,
                    label:'2. Reinforced concrete ductile coupled walls',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:3,
                    label:'3. Ordinary reinforced concrete shear walls',
                    rVal:4,
                    omega:2.5
                },
                {
                    value:4,
                    label:'4. Detailed plain concrete shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:5,
                    label:'5. Ordinary plain concrete shear walls',
                    rVal:1.5,
                    omega:2.5 
                },
                {
                    value:6,
                    label:'6. Intermediate precast shear walls',
                    rVal:4,
                    omega:2.5
                },
                {
                    value:7,
                    label:'7. Ordinary precast shear walls',
                    rVal:3,
                    omega:2.5
                },
                {
                    value:8,
                    label:'8. Special reinforced masonry shear walls',
                    rVal:5,
                    omega:2.5
                },
                {
                    value:9,
                    label:'9. Intermediate reinforced masonry shear walls',
                    rVal:3.5,
                    omega:2.5
                },
                {
                    value:10,
                    label:'10. Ordinary reinforced masonry shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:11,
                    label:'11. Detailed plain masonry shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:12,
                    label:'12. Ordinary plain masonry shear walls',
                    rVal:1.5,
                    omega:2.5
                },
                {
                    value:13,
                    label:'13. Prestressed masonry shear walls',
                    rVal:1.5,
                    omega:2.5
                },
                {
                    value:14,
                    label:'14. Ordinary reinforced AAC masonry shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:15,
                    label:'15. Ordinary plain AAC masonry shear walls',
                    rVal:1.5,
                    omega:2.5
                },
                {
                    value:16,
                    label:'16. Light-frame (wood) walls sheathed with wood structural panels rated for har resistance',
                    rVal:6.5,
                    omega:3
                },
                {
                    value:17,
                    label:'17. Light-frame (cold-formed steel) walls sheathed with wood structural panels aed for shear resistance or steel sheets',
                    rVal:6.5,
                    omega:3
                },
                {
                    value:18,
                    label:'18. Light-frame walls with shear panels of all other materials',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:19,
                    label:'19. Light-frame (cold-formed steel) wall systems using flat strap bracing',
                    rVal:4,
                    omega:2
                },
                {
                    value:20,
                    label:'20. Cross-laminated timber shear walls',
                    rVal:3,
                    omega:3
                },
                {
                    value:21,
                    label:'21. Cross-laminated timber shear walls with shear resistance provided by ih-aspect-ratio panels only',
                    rVal:4,                    
                    omega:3
                }
            ],
            subsystemB: [
                {
                    value:1,
                    label:'1. Steel eccentrically braced frames',
                    rVal:8,
                    omega:2
                },
                {
                    value:2,
                    label:'2. Steel special concentrically braced frames',
                    rVal:6,
                    omega:2
                },
                {
                    value:3,
                    label:'3. Steel ordinary concentrically braced frames',
                    rVal:3.25,
                    omega:2
                },
                {
                    value:4,
                    label:'4. Special reinforced concrete shear walls',
                    rVal:6,
                    omega:2.5
                },
                {
                    value:5,
                    label:'5. Reinforced concrete ductile coupled walls',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:6,
                    label:'6. Ordinary reinforced concrete shear walls',
                    rVal:5,
                    omega:2.5
                },
                {
                    value:7,
                    label:'7. Detailed plain concrete shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:8,
                    label:'8. Ordinary plain concrete shear walls',
                    rVal:1.5,
                    omega:2.5
                },
                {
                    value:9,
                    label:'9. Intermediate precast shear walls',
                    rVal:5,
                    omega:2.5
                },
                {
                    value:10,
                    label:'10. Ordinary precast shear walls',
                    rVal:4,
                    omega:2.5
                },
                {
                    value:11,
                    label:'11. Steel and concrete composite eccentrically braced frames',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:12,
                    label:'12. Steel and concrete composite special concentrically braced frames',
                    rVal:5,
                    omega:2
                },
                {
                    value:13,
                    label:'13. Steel and concrete composite ordinary braced frames',
                    rVal:3,
                    omega:2
                },
                {
                    value:14,
                    label:'14. Steel and concrete composite plate shear walls',
                    rVal:6.5,
                    omega:2.5
                },
                {
                    value:15,
                    label:'15. Steel and concrete composite special shear wall',
                    rVal:6,
                    omega:2.5
                },
                {
                    value:16,
                    label:'16. Steel and concrete composite ordinary shear walls',
                    rVal:5,
                    omega:2.5
                },
                {
                    value:17,
                    label:'17. Special reinforced masonry shear walls',
                    rVal:5.5,
                    omega:2.5
                },
                {
                    value:18,
                    label:'18. Intermediate reinforced masonry shear walls',
                    rVal:4,
                    omega:2.5
                },
                {
                    value:19,
                    label:'19. Ordinary reinforced masonry shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:20,
                    label:'20. Detailed plain masonry shear walls',
                    rVal:2,
                    omega:2.5
                },
                {
                    value:21,
                    label:'21. Ordinary plain masonry shear walls',
                    rVal:1.5,
                    omega:2.5
                },
                {
                    value:22,
                    label:'22. Prestressed masonry shear walls',
                    rVal:1.5,
                    omega:2.5
                },
                {
                    value:23,
                    label:'23. Light-frame (wood) walls sheathed with wood structural panels rated for shear resistance',
                    rVal:7,
                    omega:2.5
                },
                {
                    value:24,
                    label:'24. Light-frame (cold-formed steel) walls sheathed with wood structural panels rated for shear resistance or steel sheets',
                    rVal:7,
                    omega:2.5
                },
                {
                    value:25,
                    label:'25. Light-frame walls with shear panels of all other materials',
                    rVal:2.5,
                    omega:2.5
                },
                {
                    value:26,
                    label:'26. Steel buckling-restrained braced frames',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:27,
                    label:'27. Steel special plate shear walls',
                    rVal:7,
                    omega:2
                },
                {
                    value:28,
                    label:'28. Steel and concrete coupled composite plate shear walls',
                    rVal:8,
                    omega:2.5
                }
            ],
            subsystemC: [
                {
                    value:1,
                    label:'1. Steel special moment frames',
                    rVal:8,
                    omega:3
                },
                {
                    value:2,
                    label:'2. Steel special truss moment frames',
                    rVal:7,
                    omega:3
                },
                {
                    value:3,
                    label:'3. Steel intermediate moment frames',
                    rVal:4.5,
                    omega:3
                },
                {
                    value:4,
                    label:'4. Steel ordinary moment frames',
                    rVal:3.5,
                    omega:3
                },
                {
                    value:5,
                    label:'5. Special reinforced concrete moment frames',
                    rVal:8,
                    omega:3
                },
                {
                    value:6,
                    label:'6. Intermediate reinforced concrete moment frames',
                    rVal:5,
                    omega:3
                },
                {
                    value:7,
                    label:'7. Ordinary reinforced concrete moment frames',
                    rVal:3,
                    omega:3
                },
                {
                    value:8,
                    label:'8. Steel and concrete composite special moment frames',
                    rVal:8,
                    omega:3
                },
                {
                    value:9,
                    label:'9. Steel and concrete composite intermediate moment frames',
                    rVal:5,
                    omega:3
                },
                {
                    value:10,
                    label:'10. Steel and concrete composite partially restrained moment frames',
                    rVal:6,
                    omega:3
                },
                {
                    value:11,
                    label:'11. Steel and concrete composite ordinary moment frames',
                    rVal:3,
                    omega:3
                },
                {
                    value:12,
                    label:'12. Cold-formed steel—special bolted moment frame',
                    rVal:3.5,
                    omega:3
                }
            ],
            subsystemD: [
                {
                    value:1,
                    label:'1. Steel eccentrically braced frames',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:2,
                    label:'2. Steel special concentrically braced frames',
                    rVal:7,
                    omega:2.5
                },
                {
                    value:3,
                    label:'3. Special reinforced concrete shear walls',
                    rVal:7,
                    omega:2.5
                },
                {
                    value:4,
                    label:'4. Reinforced concrete ductile coupled walls',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:5,
                    label:'5. Ordinary reinforced concrete shear walls',
                    rVal:6,
                    omega:2.5
                },
                {
                    value:6,
                    label:'6. Steel and concrete composite eccentrically braced frames',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:7,
                    label:'7. Steel and concrete composite special concentrically braced frames',
                    rVal:6,
                    omega:2.5
                },
                {
                    value:8,
                    label:'8. Steel and concrete composite plate shear walls',
                    rVal:7.5,
                    omega:2.5
                },
                {
                    value:9,
                    label:'9. Steel and concrete composite special shear walls',
                    rVal:7,
                    omega:2.5
                },
                {
                    value:10,
                    label:'10. Steel and concrete composite ordinary shear walls',
                    rVal:6,
                    omega:2.5
                },
                {
                    value:11,
                    label:'11. Special reinforced masonry shear walls',
                    rVal:5.5,
                    omega:3
                },
                {
                    value:12,
                    label:'12. Intermediate reinforced masonry shear walls',
                    rVal:4,
                    omega:3
                },
                {
                    value:13,
                    label:'13. Steel buckling-restrained braced frames',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:14,
                    label:'14. Steel special plate shear walls',
                    rVal:8,
                    omega:2.5
                },
                {
                    value:15,
                    label:'15. Steel and concrete coupled composite plate shear walls',
                    rVal:8,
                    omega:2.5
                }
            ],
            subsystemE: [
                {
                    value:1,
                    label:'1. Steel special concentrically braced frames',
                    rVal:6,
                    omega:2.5
                },
                {
                    value:2,
                    label:'2. Special reinforced concrete shear walls',
                    rVal:6.5,
                    omega:2.5
                },
                {
                    value:3,
                    label:'3. Ordinary reinforced masonry shear walls',
                    rVal:3,
                    omega:3
                },
                {
                    value:4,
                    label:'4. Intermediate reinforced masonry shear walls',
                    rVal:3.5,
                    omega:3
                },
                {
                    value:5,
                    label:'5. Steel and concrete composite special concentrically braced frames',
                    rVal:5.5,
                    omega:2.5
                },
                {
                    value:6,
                    label:'6. Steel and concrete composite ordinary braced frames',
                    rVal:3.5,
                    omega:2.5
                },
                {
                    value:7,
                    label:'7. Steel and concrete composite ordinary shear walls',
                    rVal:5,
                    omega:3
                },
                {
                    value:8,
                    label:'8. Ordinary reinforced concrete shear walls',
                    rVal:5.5,
                    omega:2.5
                },
            ],
            subsystemG: [
                {
                    value:1,
                    label:'1. Steel special cantilever column systems',
                    rVal:2.5,
                    omega:2.5
                },
                {
                    value:2,
                    label:'2. Steel ordinary cantilever column systems',
                    rVal:1.25,
                    omega:1.25
                },
                {
                    value:3,
                    label:'3. Special reinforced concrete moment frames',
                    rVal:2.5,
                    omega:2.5
                },
                {
                    value:4,
                    label:'4. Intermediate reinforced concrete moment frames',
                    rVal:1.5,
                    omega:1.5
                },
                {
                    value:5,
                    label:'5. Ordinary reinforced concrete moment frames',
                    rVal:1,
                    omega:1.25
                },
                {
                    value:6,
                    label:'6. Timber frames',
                    rVal:1.5,
                    omega:1.5
                }	
            ]
        }
    },
}
</script>